/**
 * A stored session -> the same feed a socket would fill.
 *
 * The frames on disk are the frames that went on the wire — one `emit()` fed
 * both — so replay is a matter of pacing them, not of interpreting them. Phase
 * 1 read a local file; phase 6 adds the stored session, fetched from the same
 * bucket the round wrote to.
 *
 * Both sources produce a `SessionLog` and hand its frames to the same player, so
 * the renderer still cannot tell live from replay, or a dropped file from a
 * session id typed into a picker.
 *
 * The header line is the other half of that claim. A version-2 log carries its
 * own catalogue, so the dropped file needs no server at all — not even for the
 * ingredient icons, which until now came from whatever `/api/config` happened
 * to be serving at the moment somebody watched.
 */

import type {
  EventFrame,
  SessionIndex,
  SessionLog,
  SessionLogHeader,
  SessionManifest,
} from '@overcooked/shared'
import { TICK_MS } from '@overcooked/shared'
import type { EventFeed } from '../feed/feed'
import { tickToSeconds } from '../render/hudState'
import type { ReplayIndex } from './scrub'
import { indexFrames, seekTo } from './scrub'
import type { BatchFilter } from './sessionTabs'
import { inviteParam } from './sessionTabs'

/**
 * A stored log, split into the one line that describes the session and the
 * many that are the session.
 *
 * Exactly one new shape over what phase 1 read, discriminated by `kind`. A
 * file recorded before the header existed still parses — it just yields
 * `header: null`, which is the signal to fall back to the live catalogue and
 * the separate manifest fetch, the way this page always used to work.
 */
export function parseLog(text: string): SessionLog {
  let header: SessionLogHeader | null = null
  const frames: EventFrame[] = []

  for (const raw of text.split('\n')) {
    const line = raw.trim()
    if (line.length === 0) continue
    const record = JSON.parse(line) as EventFrame | SessionLogHeader
    if ((record as SessionLogHeader).kind === 'header') {
      header = record as SessionLogHeader
      continue
    }
    frames.push(record as EventFrame)
  }

  return { header, frames }
}

/**
 * The session picker's data source: one small JSON, no logs opened.
 *
 * The one endpoint on this page that is behind the admin password. Listing every
 * round the study has recorded is a different thing from opening the one round
 * somebody was sent a link to, and only the list needed a door — so the picker
 * can be refused while `/replay/<id>` below it still works. The refusal is
 * turned into a sentence here rather than a status code, because the page prints
 * whatever this throws.
 *
 * `invite` narrows the list to one batch on the server (`''` for "No batch");
 * left out, the request is exactly the one this page always made.
 */
export async function fetchSessionIndex(
  filter: { invite?: BatchFilter } = {},
  fetcher: typeof fetch = fetch,
): Promise<SessionIndex> {
  const batch = inviteParam(filter.invite)
  const response = await fetcher(batch ? `/api/sessions?${batch}` : '/api/sessions')
  if (response.status === 401) {
    throw new Error('Log in on the admin page to see this list.')
  }
  if (response.status === 503) {
    throw new Error('Admin is turned off on this server, so the list is too. Replay links still work.')
  }
  if (!response.ok) throw new Error(`The server returned an error (${response.status}).`)
  return (await response.json()) as SessionIndex
}

/**
 * An old replay link — a session id from before ids carried a random suffix —
 * opens for an admin only, because it can be guessed. Said as a sentence the
 * page can print, like the index's refusal above.
 */
export function legacyRefusal(status: number): string | null {
  if (status === 401) return 'This is an older replay link. Log in on the admin page to open it.'
  if (status === 503) return 'This is an older replay link. It needs an admin login, which is turned off on this server.'
  return null
}

export async function fetchSessionManifest(
  sessionId: string,
  fetcher: typeof fetch = fetch,
): Promise<SessionManifest> {
  const url = `/api/sessions/${encodeURIComponent(sessionId)}/manifest`
  const response = await fetcher(url)
  const refused = legacyRefusal(response.status)
  if (refused) throw new Error(refused)
  if (!response.ok) throw new Error(`The server returned an error (${response.status}).`)
  return (await response.json()) as SessionManifest
}

export async function fetchSessionLog(sessionId: string, fetcher: typeof fetch = fetch): Promise<SessionLog> {
  const response = await fetcher(`/api/sessions/${encodeURIComponent(sessionId)}/log`)
  if (response.status === 404) throw new Error('No game log is saved for this round.')
  const refused = legacyRefusal(response.status)
  if (refused) throw new Error(refused)
  if (!response.ok) throw new Error(`The server returned an error (${response.status}).`)
  return parseLog(await response.text())
}

export const audioUrl = (sessionId: string, playerId: string): string =>
  `/api/sessions/${encodeURIComponent(sessionId)}/audio/${encodeURIComponent(playerId)}`

/**
 * As much of `HTMLAudioElement` as the transport actually drives.
 *
 * Naming the four members rather than the element is what lets the correction
 * loop be asserted against a plain object in a test runner with no DOM, and it
 * states the contract: the player writes `currentTime` and `playbackRate` and
 * calls `play`/`pause`. It never reads back anything it did not write, except
 * `currentTime`, which is the one thing only the browser knows.
 */
export interface ReplayAudio {
  currentTime: number
  playbackRate: number
  play(): void | Promise<unknown>
  pause(): void
}

/**
 * Start an element without leaving its promise unhandled.
 *
 * `HTMLMediaElement.play()` returns a promise that rejects with `AbortError`
 * whenever a `pause()` or a new `src` lands before playback actually began —
 * which is every seek, every speed change and every swap of a session's audio
 * on this page — and with `NotAllowedError` before the first gesture. Neither
 * is a fault: the transport has already moved on, and the next `play()` will
 * start the element where it belongs. `void element.play()` let both escape as
 * uncaught exceptions in the console. Anything else is still worth a warning.
 */
export function playQuietly(element: Pick<ReplayAudio, 'play'>): void {
  let started: void | Promise<unknown>
  try {
    started = element.play()
  } catch (err) {
    console.warn('[replay] audio would not play:', err)
    return
  }
  if (started && typeof (started as Promise<unknown>).catch === 'function') {
    ;(started as Promise<unknown>).catch((err: { name?: string }) => {
      if (err?.name === 'AbortError' || err?.name === 'NotAllowedError') return
      console.warn('[replay] audio would not play:', err)
    })
  }
}

/** How far an element may drift from the playhead before it is re-anchored. */
export const AUDIO_DRIFT_TOLERANCE_S = 0.1

/** How often drift is checked, in wall-clock milliseconds. */
export const AUDIO_CORRECTION_INTERVAL_MS = 1000

export interface ReplayPlayer {
  readonly index: ReplayIndex
  /** Where playback currently is, in game ticks. */
  tick(): number
  playing(): boolean
  play(): void
  pause(): void
  /** Jump. Cheap: one keyframe plus at most two seconds of events. */
  seek(tick: number): void
  setSpeed(speed: number): void
  stop(): void
  /**
   * Hand the transport the voices to move with the picture.
   *
   * Called again whenever the elements change — a different session, or a
   * `<audio>` that has only now mounted — and it replaces the set outright, so
   * an element that has gone away stops being driven. `syncBeepTick` is the
   * origin: the tick the anchor tone was heard on.
   */
  attachAudio(elements: ReplayAudio[], syncBeepTick: number | null): void
}

/**
 * Paces frames against a wall clock exactly as the server's loop produced them:
 * a frame is released once `tick * TICK_MS` of playback time has elapsed.
 *
 * Seeking resets the feed and re-pushes from the nearest preceding keyframe,
 * which is the same thing a client joining mid-round does with the keyframe the
 * server sends it. There is no separate seek path to keep in step.
 */
export function createPlayer(
  frames: EventFrame[],
  feed: EventFeed,
  options: {
    onProgress?: (tick: number, playing: boolean) => void
    now?: () => number
    schedule?: (fn: () => void) => number
    cancel?: (handle: number) => void
  } = {},
): ReplayPlayer {
  const index = indexFrames(frames)
  const now = options.now ?? (() => performance.now())
  const schedule =
    options.schedule ?? ((fn: () => void) => requestAnimationFrame(fn) as unknown as number)
  const cancel = options.cancel ?? ((handle: number) => cancelAnimationFrame(handle))

  let speed = 1
  let cursor = 0
  let baseTick = index.firstTick
  let startedAt = now()
  let running = false
  let handle = 0
  let currentTick = index.firstTick

  // The voices. Empty until a page hands them over, which is why every audio
  // helper below is a no-op loop rather than a branch: a player with no audio
  // is the same player.
  let audio: ReplayAudio[] = []
  let syncBeepTick: number | null = null
  let correctedAt = now()

  const audioSecondsNow = (): number => tickToSeconds(currentTick, syncBeepTick)

  /** Put every element exactly where the playhead says it should be. */
  const anchorAudio = (): void => {
    const at = audioSecondsNow()
    for (const element of audio) element.currentTime = at
    correctedAt = now()
  }

  const startAudio = (): void => {
    anchorAudio()
    for (const element of audio) {
      element.playbackRate = speed
      playQuietly(element)
    }
  }

  const stopAudio = (): void => {
    for (const element of audio) element.pause()
  }

  /**
   * The difference between "roughly together" and "together".
   *
   * `playbackRate` is a request, not a guarantee — browsers resample at 0.5×
   * and 2× with their own accuracy — so an element left alone slides away from
   * the picture over a long review. Once a second, anything more than a tenth
   * of a second out is put back. Under the tolerance nothing is touched,
   * because writing `currentTime` is audible.
   */
  const correctDrift = (): void => {
    if (audio.length === 0) return
    if (now() - correctedAt < AUDIO_CORRECTION_INTERVAL_MS) return
    correctedAt = now()
    const at = audioSecondsNow()
    for (const element of audio) {
      if (Math.abs(element.currentTime - at) > AUDIO_DRIFT_TOLERANCE_S) element.currentTime = at
    }
  }

  const report = (): void => options.onProgress?.(currentTick, running)

  const step = (): void => {
    const elapsed = ((now() - startedAt) * speed) / TICK_MS
    const target = Math.min(baseTick + elapsed, index.lastTick)
    while (cursor < index.frames.length && index.frames[cursor].tick <= target) {
      feed.push(index.frames[cursor])
      cursor += 1
    }
    currentTick = target
    correctDrift()
    report()

    if (running && cursor < index.frames.length) handle = schedule(step)
    else if (running) {
      running = false
      stopAudio()
      report()
    }
  }

  const restartClock = (): void => {
    baseTick = currentTick
    startedAt = now()
  }

  return {
    index,
    tick: () => currentTick,
    playing: () => running,
    play() {
      if (running || index.frames.length === 0) return
      // Replaying from the end is what "play again" means at the end of a log.
      if (cursor >= index.frames.length) this.seek(index.firstTick)
      running = true
      restartClock()
      startAudio()
      handle = schedule(step)
      report()
    },
    pause() {
      if (!running) return
      running = false
      cancel(handle)
      stopAudio()
      report()
    },
    seek(tick: number) {
      const clamped = Math.max(index.firstTick, Math.min(tick, index.lastTick))
      const jump = seekTo(index, clamped)
      feed.reset()
      for (const frame of jump.frames) feed.push(frame)
      cursor = jump.cursor
      currentTick = clamped
      restartClock()
      // The conversation must land on the moment the picture landed on, and it
      // must do so whether or not the transport is running — a scrub while
      // paused is how somebody finds a moment to listen to.
      anchorAudio()
      report()
    },
    setSpeed(next: number) {
      speed = next > 0 ? next : 1
      restartClock()
      for (const element of audio) element.playbackRate = speed
    },
    stop() {
      running = false
      cancel(handle)
      stopAudio()
    },
    attachAudio(elements: ReplayAudio[], beepTick: number | null) {
      // Elements that are leaving stop first, so a session swapped out mid-play
      // does not keep talking over the one that replaced it.
      for (const element of audio) if (!elements.includes(element)) element.pause()
      audio = elements
      syncBeepTick = beepTick
      if (running) startAudio()
      else anchorAudio()
    },
  }
}
